/**
 * SG 頁的狀態。刻意做成一個 hook（跟 `useConsoleActions` 一樣的職責切法）：
 * 元件只收 props、不自己 fetch，這樣「同一份資料只有一個來源」。
 *
 * 三塊狀態各自獨立載入，互不阻塞：
 *   1. 女孩清單（經 `/api/sg-proxy`，金鑰由 Pages Function 注入；代理不在就翻 `proxyReady`）
 *   2. 已接上的 session（只要一般登入）
 *   3. 每個 session 的模式＋最近草稿（按需載入，只有展開的才抓）
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotices, type Notice } from '@chat-rea/console-ui';
import { API_BASE, IS_MOCK } from '../api';
import * as sg from './sgApi';
import {
  parseMode,
  recentDrafts,
  type AutoReplyMode,
  type AutoReplyStatus,
  type SgConversation,
  type SgDraft,
  type SgGirl,
  type SgSession,
} from './types';

export type SugargirlState = {
  isMock: boolean;
  /** 後端位址是否可用（mock 模式就沒有）。 */
  live: boolean;
  /**
   * SG 代理是否可用。代理是 Pages Functions 的一部分，正式站一開始就是 true；
   * 只有在代理回非 JSON／404（本機 `vite dev` 沒 Functions）時才翻成 false，
   * 畫面就顯示「代理不在」，而不是要使用者手動貼金鑰（那條後門已移除）。
   */
  proxyReady: boolean;

  girls: SgGirl[];
  girlsLoading: boolean;
  girlsError: string | null;

  /** 展開中的女孩 id（一次一個，避免十幾條對話串同時打站方）。 */
  expandedGirlId: string | null;
  conversations: SgConversation[];
  conversationsLoading: boolean;
  conversationsError: string | null;

  sessions: SgSession[];
  sessionsLoading: boolean;
  sessionsError: string | null;

  /** sessionId → 模式／人設（展開才抓）。 */
  status: Record<string, AutoReplyStatus>;
  /** sessionId → 全部草稿（畫面只顯示最近 3 則）。 */
  drafts: Record<string, SgDraft[]>;
  detailLoading: Record<string, boolean>;
  detailError: Record<string, string | null>;

  /** 進行中的動作（同一個 session 的按鈕一起 disable）。 */
  busy: string | null;
  /** 通知佇列（跟收件匣共用同一個資料形狀與規則）。 */
  notices: Notice[];
  notice: string | null;

  refresh: () => Promise<void>;
  toggleGirl: (girlId: string) => Promise<void>;
  attach: (input: { girlId: string; conversationId: string; mode: AutoReplyMode }) => Promise<void>;
  detach: (sessionId: string) => Promise<void>;
  setMode: (sessionId: string, mode: AutoReplyMode) => Promise<void>;
  reloadDetail: (sessionId: string) => Promise<void>;
  dismissNotice: (id: string) => void;
};

const errText = (err: unknown): string => (err instanceof Error ? err.message : String(err));

export function useSugargirl(): SugargirlState {
  const live = !IS_MOCK && API_BASE !== '';
  const [proxyReady, setProxyReady] = useState(true);

  const [girls, setGirls] = useState<SgGirl[]>([]);
  const [girlsLoading, setGirlsLoading] = useState(false);
  const [girlsError, setGirlsError] = useState<string | null>(null);

  const [expandedGirlId, setExpandedGirlId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<SgConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState<string | null>(null);

  const [sessions, setSessions] = useState<SgSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const [status, setStatus] = useState<Record<string, AutoReplyStatus>>({});
  const [drafts, setDrafts] = useState<Record<string, SgDraft[]>>({});
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>({});
  const [detailError, setDetailError] = useState<Record<string, string | null>>({});

  const [busy, setBusy] = useState<string | null>(null);
  /** 舊的單條橫幅退場：改成佇列，多筆失敗不會互相蓋掉。 */
  const notices = useNotices();
  const [notice, setNotice] = useState<string | null>(null);

  /** 掛載後才抓；避免 StrictMode 的雙次 effect 各打一輪（第二次看到 ref 就跳過）。 */
  const started = useRef(false);

  const loadSessions = useCallback(async () => {
    if (!live) return;
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      setSessions(await sg.listSgSessions());
    } catch (err) {
      setSessionsError(errText(err));
    } finally {
      setSessionsLoading(false);
    }
  }, [live]);

  const loadGirls = useCallback(async () => {
    if (!live) return;
    setGirlsLoading(true);
    setGirlsError(null);
    try {
      setGirls(await sg.listGirls());
      setProxyReady(true);
    } catch (err) {
      if (err instanceof sg.SgProxyUnavailableError) setProxyReady(false);
      setGirlsError(errText(err));
    } finally {
      setGirlsLoading(false);
    }
  }, [live]);

  const reloadDetail = useCallback(async (sessionId: string) => {
    if (!live || sessionId === '') return;
    setDetailLoading((prev) => ({ ...prev, [sessionId]: true }));
    setDetailError((prev) => ({ ...prev, [sessionId]: null }));
    try {
      const [nextStatus, nextDrafts] = await Promise.all([
        sg.getAutoReply(sessionId),
        sg.listDrafts(sessionId),
      ]);
      setStatus((prev) => ({ ...prev, [sessionId]: nextStatus }));
      setDrafts((prev) => ({ ...prev, [sessionId]: nextDrafts }));
      // 清單上的模式也要跟著更新（sessions 是清單的真相來源）。
      setSessions((prev) => prev.map((item) => (item.id === sessionId ? { ...item, mode: nextStatus.mode } : item)));
    } catch (err) {
      setDetailError((prev) => ({ ...prev, [sessionId]: errText(err) }));
    } finally {
      setDetailLoading((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, [live]);

  const refresh = useCallback(async () => {
    setNotice(null);
    await Promise.all([loadGirls(), loadSessions()]);
  }, [loadGirls, loadSessions]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void refresh();
  }, [loadGirls, loadSessions]);

  // 已開的卡片先抓一次模式／草稿，否則「最近 3 則草稿」要等到展開才有內容。
  useEffect(() => {
    if (!live) return;
    for (const item of sessions) {
      if (status[item.id] === undefined && detailLoading[item.id] !== true) {
        void reloadDetail(item.id);
      }
    }
  }, [sessions, status, detailLoading, live, reloadDetail]);

  const toggleGirl = useCallback(async (girlId: string) => {
    if (expandedGirlId === girlId) {
      setExpandedGirlId(null);
      setConversations([]);
      setConversationsError(null);
      return;
    }
    setExpandedGirlId(girlId);
    setConversations([]);
    setConversationsError(null);
    if (!live) return;
    setConversationsLoading(true);
    try {
      setConversations(await sg.listGirlConversations(girlId));
    } catch (err) {
      setConversationsError(errText(err));
    } finally {
      setConversationsLoading(false);
    }
  }, [expandedGirlId, live]);

  const attach = useCallback(async (input: { girlId: string; conversationId: string; mode: AutoReplyMode }) => {
    setBusy(`attach:${input.girlId}`);
    setNotice(null);
    try {
      const created = await sg.attachGirl(input);
      setSessions((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      setNotice(`已接上 ${created.girlId}（${parseMode(created.mode, 'draft') === 'draft' ? '擬稿' : '自動'}模式），AI 回覆會出現在這張卡上`);
      void reloadDetail(created.id);
    } catch (err) {
      notices.push({ kind: 'error', message: `接上 ${input.girlId} 失敗：${errText(err)}` });
    } finally {
      setBusy(null);
    }
  }, [reloadDetail]);

  const detach = useCallback(async (sessionId: string) => {
    setBusy(`detach:${sessionId}`);
    setNotice(null);
    try {
      await sg.detachSession(sessionId);
      setSessions((prev) => prev.filter((item) => item.id !== sessionId));
      setStatus((prev) => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
      setNotice('已退出（session 已刪除）');
    } catch (err) {
      notices.push({ kind: 'error', message: `退出失敗：${errText(err)}` });
    } finally {
      setBusy(null);
    }
  }, []);

  const setMode = useCallback(async (sessionId: string, mode: AutoReplyMode) => {
    setBusy(`mode:${sessionId}`);
    try {
      const applied = await sg.setAutoReply(sessionId, mode);
      setStatus((prev) => {
        const current = prev[sessionId];
        return current === undefined ? prev : { ...prev, [sessionId]: { ...current, mode: applied } };
      });
      setSessions((prev) => prev.map((item) => (item.id === sessionId ? { ...item, mode: applied } : item)));
      setNotice(
        applied === 'draft'
          ? '已切成擬稿：AI 只寫草稿，不會送出'
          : '已切成自動：AI 寫完直接送回 SG，客人會立刻收到',
      );
    } catch (err) {
      notices.push({ kind: 'error', message: `切換模式失敗：${errText(err)}` });
    } finally {
      setBusy(null);
    }
  }, []);

  /** 女孩卡片要顯示的配對：girlId → session（一個女孩同時只接一條）。 */
  const sessionByGirl = useMemo(() => {
    const map = new Map<string, SgSession>();
    for (const item of sessions) map.set(item.girlId, item);
    return map;
  }, [sessions]);

  return {
    isMock: IS_MOCK,
    live,
    proxyReady,
    girls,
    girlsLoading,
    girlsError,
    expandedGirlId,
    conversations,
    conversationsLoading,
    conversationsError,
    // 展開／接上的排序交給畫面；這裡只提供資料與 sessionByGirl 的來源。
    sessions: [...sessionByGirl.values()],
    sessionsLoading,
    sessionsError,
    status,
    drafts,
    detailLoading,
    detailError,
    busy,
    notices: notices.notices,
    notice,
    refresh,
    toggleGirl,
    attach,
    detach,
    setMode,
    reloadDetail,
    dismissNotice: notices.dismiss,
  };
}

/** 卡片上的「最近 3 則草稿」。 */
export function draftsOf(state: SugargirlState, sessionId: string): SgDraft[] {
  return recentDrafts(state.drafts[sessionId] ?? [], 3);
}
